import i18n from 'i18next';
import { action, computed, observable, toJS } from 'mobx';
import moment from 'moment';
import api from '../api';
import EventDiscountTypeEnum from '../enums/EventDiscountTypeEnum';
import EventTypeEnum from '../enums/EventTypeEnum';
import ReceiveMethodEnum from '../enums/ReceiveMethodEnum';
import StoreModel from '../models/StoreModel';
import defaultImage from '../static/images/defaultImage.png';
import { applyMenuDataSorting, checkUseTime } from '../util/index';

class MenuStore {
  constructor(root) {
    this.root = root;
  }

  @observable storeData; // 상점 및 메뉴 데이터
  @observable selectedMenu; // 선택한 메뉴
  @observable menuGroupIndex = 0; // 메뉴 그룹 탭의 현재 인덱스
  @observable qty = 1; // 메뉴 수량
  @observable filterdMenuGroups; //그룹 사용 시간 필터 데이터
  @observable isSetItemModalOpen; // 메뉴 아이템 링크 모달 오픈 여부

  // 상점 문화권별 금액 기호
  @computed
  get currencySymbol() {
    if (this.storeData && this.storeData.culture) {
      switch (this.storeData.culture) {
        case 'ko-KR':
          return '₩';
        case 'en-US':
          return '$';
        case 'ja-JP':
          return '¥';
        case 'zh-CN':
          return '¥';
        case 'th-TH':
          return '฿';
        case 'vi-VN':
          return '₫';
        default:
          return '';
      }
    } else {
      return '';
    }
  }

  // 상점 문화권별 통화 단위
  @computed
  get currencyUnit() {
    switch (this.storeData.culture) {
      case 'ko-KR':
        return '원';
      default:
        return '';
    }
  }

  // 매장 오픈 여부
  @computed
  get isOpenStore() {
    if (this.storeData) {
      // 현재 요일 코드
      const currentWeek = Number(moment().format('e')); // 0~6, 일~토

      // 현재 요일의 운영시간 조회
      const businessTime = this.storeData.businessTimes[currentWeek];

      if (businessTime.isClosed === '1') {
        return false;
      } else {
        return checkUseTime(businessTime.startTime, businessTime.endTime);
      }
    }

    return false;
  }

  // 현재 선택한 수령방법별 경감세금 사용 여부
  @computed
  get isReduceTax() {
    if (
      this.storeData.culture === 'ja-JP' &&
      ((this.root.cartStore.cartData.receiveMethodCode === ReceiveMethodEnum.EATIN &&
        this.storeData.eatinTaxType === '1') ||
        (this.root.cartStore.cartData.receiveMethodCode === ReceiveMethodEnum.TAKEOUT &&
          this.storeData.takeoutTaxType === '1') ||
        (this.root.cartStore.cartData.receiveMethodCode === ReceiveMethodEnum.DELIVERY &&
          this.storeData.deliveryTaxType === '1'))
    ) {
      return true;
    }
    return false;
  }

  // 데이터 조회 및 세팅
  @action
  async setStoreData() {
    // 상점 및 메뉴 데이터 조회
    if (!this.storeData) {
      try {
        if (!this.root.commonStore.companyId || !this.root.commonStore.storeCode) {
          throw new Error(i18n.t('common.error.configNotFound'));
        }

        const result = await api.getStore(
          this.root.commonStore.companyId,
          this.root.commonStore.storeCode,
          i18n.languages[0],
        );

        // 매장 존재 여부 검사
        if (result.status && result.status === 404) {
          throw new Error(i18n.t('common.error.configNotFound'));
        }

        // 매장 조회 데이터 파싱
        const storeModel = new StoreModel(result.data);

        // 메뉴 데이터 정렬
        applyMenuDataSorting(storeModel);

        // 링크메뉴 세팅
        storeModel.menus.forEach(menu => {
          if (menu.linkItemCode) {
            menu.linkMenu = storeModel.menus.find(m => m.itemCode === menu.linkItemCode);
          }
        });

        // 매장 데이터 설정
        this.storeData = storeModel;

        // 수령방법을 첫 번쨰로 자동 선택
        if ((this.storeData.orderType & 1) === 1) {
          this.root.cartStore.cartData.receiveMethodCode = ReceiveMethodEnum.EATIN;
        } else if ((this.storeData.orderType & 2) === 2) {
          this.root.cartStore.cartData.receiveMethodCode = ReceiveMethodEnum.TAKEOUT;
        } else if ((this.storeData.orderType & 4) === 4) {
          this.root.cartStore.cartData.receiveMethodCode = ReceiveMethodEnum.DELIVERY;
        }

        // Manifest 파일 업데이트
        this.updateManifest(this.root.commonStore.companyId, this.root.commonStore.storeCode);
      } catch (error) {
        this.root.commonStore.setError(error);
        throw error;
      }
    }
  }

  // 메뉴 그룹 인덱스 변경
  @action
  setMenuGroupIndex(index) {
    this.menuGroupIndex = index;
  }

  @action
  setSelectedMenu(menu) {
    this.selectedMenu = menu;
  }

  /**
   * 선택한 메뉴 데이터 설정
   * @param {string} itemCode 아이템 코드
   */
  @action
  setSelectedMenuByItemCode(itemCode, groupCode) {
    // 수량 초기화
    this.qty = 1;

    // 메뉴 데이터 복사
    const menu = toJS(this.getMenu(itemCode, groupCode), { recurseEverything: true });
    menu.storeCode = menu.itemStoreCode;

    // 수량 및 토핑, 세트메뉴 세팅
    menu.toppingGroups = this.getToppingGroups(menu.toppingTemplateCode);
    menu.toppingGroups.forEach(toppingGroup => {
      toppingGroup.toppings = this.getToppings(toppingGroup.groupCode);
      toppingGroup.toppings.forEach(topping => {
        if (topping.isSelect && !topping.isSoldOut) {
          topping.isChecked = true;
          topping.qty = 1;
        } else {
          topping.isChecked = false;
          topping.qty = 0;
        }
      });
    });
    menu.setMenuGroups = this.getSetMenuGroups(itemCode);
    menu.setMenuGroups.forEach(setMenuGroup => {
      setMenuGroup.setMenus = this.getSetMenus(itemCode, setMenuGroup.groupCode);
      setMenuGroup.setMenus.forEach(setMenu => (setMenu.isChecked = false));
    });

    // 선택한 메뉴 데이터 저장
    this.selectedMenu = menu;
  }

  /**
   * 토핑 선택
   * @param {StoreToppingGroupModel} toppingGroup 토핑의 그룹 데이터
   * @param {StoreToppingModel} topping 토핑 데이터
   */
  @action
  checkTopping(toppingGroup, topping) {
    if (topping.isChecked) {
      // 토핑 선택 해제
      topping.isChecked = false;
    } else {
      // 토핑 선택 하는 경우 토핑 개수 체크
      const selectedCount = toppingGroup.toppings.filter(topping => topping.isChecked === true).length;
      if ((toppingGroup.maxCount > 0 && toppingGroup.maxCount > selectedCount) || toppingGroup.maxCount === 0) {
        topping.isChecked = true;
      } else {
        this.root.commonStore.setSnackbar(true, i18n.t('menu.error.optionMaxExceeded', { n: toppingGroup.maxCount }));
      }
    }
  }

  /**
   * 세트메뉴 선택
   * @param setMenuGroup 세트메뉴 그룹 데이터
   * @param setMenu 세트메뉴 데이터
   */
  @action
  checkSetMenu(setMenuGroup, setMenu) {
    setMenuGroup.setMenus.forEach(setMenu => (setMenu.isChecked = false));
    setMenu.isChecked = true;
  }

  /**
   * 메뉴 수량 변경
   * @param {number} qty 수량
   */
  @action
  setMenuQty(qty) {
    this.qty = qty;
  }

  // 메뉴 그룹 시간 설정 체크하여 표시/숨김 필터링
  @action
  filterMenuGroups() {
    this.filterdMenuGroups = this.storeData.menuGroups.filter(menuGroup => {
      if (menuGroup.useType === '1') {
        // 메뉴그룹 사용시간별
        return checkUseTime(menuGroup.startUseTime, menuGroup.endUseTime);
      } else if (menuGroup.useType === '2') {
        // 메뉴그룹 요일별
        // 저장된 설정 값이 없는 경우 표시
        if (!menuGroup.useTime) return true;

        // 현재요일 설정 조회
        const useTime = menuGroup.useTime[new Date().getDay()];

        // 해당 요일 시간설정을 사용하지 않는다면 표시
        if (!useTime.IsUsed) return true;

        // 사용시간 이내라면 표시
        return checkUseTime(useTime.StartTime, useTime.EndTime);
      }

      return true;
    });
  }

  @action
  openSetItemModal() {
    this.isSetItemModalOpen = true;
  }

  @action
  closeSetItemModal() {
    this.isSetItemModalOpen = false;
  }

  // manifest.json 파일 업데이트
  updateManifest(companyId, storeCode) {
    if (companyId && storeCode) {
      var myDynamicManifest = {
        name: `Up스마트오더 - ${this.storeData.storeName}`,
        short_name: `${this.storeData.storeName}`,
        start_url: `${
          window.location.protocol +
          '//' +
          window.location.hostname +
          (window.location.port ? ':' + window.location.port : '')
        }/${companyId}/${storeCode}`,
        display: 'standalone',
        background_color: '#000000',
        theme_color: '#ffffff',
        icons: [
          {
            src: 'logo_180.png',
            sizes: '180x180',
            type: 'image/jpg',
          },
        ],
      };
      const stringManifest = JSON.stringify(myDynamicManifest);
      const blob = new Blob([stringManifest], { type: 'application/json' });
      const manifestURL = URL.createObjectURL(blob);
      document.querySelector('#manifest-placeholder').setAttribute('href', manifestURL);
    }
  }

  /**
   * 해당 문화권의 금액 포맷으로 변환
   * @param {number} value 포맷을 변경할 금액
   * @returns
   */
  numberFormatByLocale(value) {
    const formatter = new Intl.NumberFormat([this.storeData.culture, 'ko-KR']);
    return `${formatter.format(value || 0)}`;
  }
  /**
   * 해당 문화권의 금액 포맷으로 변환
   * @param {number} value 포맷을 변경할 금액
   * @returns
   */
  formatCurrency(value) {
    const formatter = new Intl.NumberFormat([this.storeData.culture, 'en-US']);
    return `${this.currencySymbol} ${formatter.format(value || 0)}`;
  }

  /**
   * 메뉴 리스트 조회
   * @param {string} groupCode 그룹코드
   * @returns 메뉴 리스트
   */
  getMenus(groupCode) {
    return this.storeData.menus.filter(menu => menu.groupCode === groupCode);
  }

  /**
   * 메뉴 조회
   * @param {stgring} itemCode 메뉴 아이템 코드
   * @param {string} groupCode 메뉴 그룹 코드 있는 경우
   * @returns 메뉴 데이터
   */
  getMenu(itemCode, groupCode) {
    return this.storeData.menus.find(
      menu => menu.itemCode === itemCode && (groupCode ? menu.groupCode === groupCode : true),
    );
  }

  /**
   * 메뉴별 토핑 그룹 리스트 조회
   * @param {string} templateCode 토핑 템플릿 코드
   * @returns 토핑 그룹 리스트
   */
  getToppingGroups(templateCode) {
    return this.storeData.toppingGroups.filter(toppingGroup => toppingGroup.templateCode === templateCode);
  }

  /**
   * 토핑 그룹별 토핑 리스트 조회
   * @param {string} toppingGroupCode 토핑 그룹 코드
   * @returns 토핑 리스트
   */
  getToppings(toppingGroupCode) {
    return this.storeData.toppings.filter(topping => topping.groupCode === toppingGroupCode);
  }

  /**
   * 메뉴별 세트메뉴 그룹 리스트 조회
   * @param {string} itemCode 메뉴 아이템 코드
   * @returns 세트메뉴 그룹 리스트
   */
  getSetMenuGroups(itemCode) {
    return this.storeData.setMenuGroups.filter(setMenuGroup => setMenuGroup.itemCode === itemCode);
  }

  /**
   * 세트메뉴 그룹별 세트메뉴 리스트 조회
   * @param {string} itemCode 메뉴 아이템 코드
   * @param {string} setMenuGroupCode  세트메뉴 그룹 코드
   * @returns 세트메뉴 리스트
   */
  getSetMenus(itemCode, setMenuGroupCode) {
    return this.storeData.setMenus.filter(
      setMenu => setMenu.itemCode === itemCode && setMenu.groupCode === setMenuGroupCode,
    );
  }

  // 메뉴 이미지 URL 조회
  getImageUrl(itemStoreCode, pictureFileName1, pictureFileName2) {
    const baseURL = `${process.env.REACT_APP_UPCLOUD_API_URL}/PosImage2`;
    let filePath = `/${this.storeData.companyId}/${itemStoreCode}/BasicSetting`;

    if (pictureFileName1) {
      filePath += `/MenuKiosk/${pictureFileName1}`;
      return baseURL + filePath;
    } else if (pictureFileName2) {
      filePath += `/Menu/${pictureFileName2}`;
      return baseURL + filePath;
    } else {
      return defaultImage;
    }
  }

  /**
   * 메뉴 단가 조회
   * 일본 문화권 매장의 경우 경감세금 적용
   * @param menu 메뉴 데이터
   * @returns 메뉴 단가
   */
  getMenuPrice(menu) {
    // 매장이 경감세금으로 설정되어 있고 메뉴에서 경감세금 사용 중이며 메뉴에 경감세금 단가가 설정되어 있는경우 경감세금 단가 사용
    if (this.isReduceTax && menu.isTax2 === '1' && menu.rdPrice > 0) {
      return menu.rdPrice;
    } else {
      return menu.price;
    }
  }

  /**
   * 토핑 금액 조회
   * @param {Topping} topping 토핑 데이터
   * @returns 토핑 금액
   */
  getToppingPrice(topping) {
    // 매장이 경감세금으로 설정되어 있고 토핑이 경감세금 추가 단가가 설정되어 있으면 경감세금 추가 단가 사용
    if (this.isReduceTax && topping.addRdPrice > 0) {
      return topping.addRdPrice;
    }

    return topping.addPrice;
  }

  /**
   * 세트메뉴 추가 금액 계산 (키오스크의 경우 세트메뉴의 포장하기 추가 금액만 사용하기 때문에 동일하게 설정)
   * @param {SetMenu} setMenu 세트메뉴 데이터
   * @returns
   */
  getSetMenuPrice(setMenu) {
    // 일본 문화권인 경우 수령방법별 추가금액을 사용하지 않고 경감세금 단가를 사용한다.
    if (this.storeData.culture === 'ja-JP') {
      // 일본 문화권, 포장하기에 경감세금으로 설정, 경감세금 추가 단가가 설정되어 있는 경우 경감세금 추가 단가 사용
      if (this.storeData.takeoutTaxType === '1' && setMenu.addRdPrice > 0) {
        return setMenu.addRdPrice;
      }
    } else if (setMenu.takeOutAddPrice > 0) {
      // 일본 매장이 아닌 경우 포장하기 추가 단가 사용
      return setMenu.takeOutAddPrice;
    }

    return setMenu.addPrice;
  }

  /**
   * 메뉴 금액 정보 조회
   * @param menus 메뉴 데이터
   */
  getMenuPriceInfo = menu => {
    const price = this.getMenuPrice(menu); // 가격 가져오기
    let discountName = '';
    let discountAmount = 0;
    let discountPercentage = 0;

    // 행사 할인 적용
    const event = this.storeData.eventMap.get(menu.itemCode);
    if (event) {
      discountName = event.eventName;
      if (event.eventType === EventTypeEnum.EVENT_PRICE) {
        // 행사단가할인
        discountAmount = price - event.eventPrice;
        discountPercentage = Math.round((discountAmount / price) * 100);
      } else if (
        event.eventType === EventTypeEnum.ITEM_DISCOUNT &&
        event.discountType === EventDiscountTypeEnum.AMOUNT &&
        event.discountAmount <= price
      ) {
        // 품목단위할인 - 금액할인
        discountAmount = event.discountAmount;
        discountPercentage = Math.round((discountAmount / price) * 100);
      } else if (event.eventType === EventTypeEnum.ITEM_DISCOUNT && event.discountType === EventDiscountTypeEnum.RATE) {
        // 품목단위할인 - 비율할인
        discountAmount = Math.floor(price * event.discountRate);
        discountPercentage = event.discountRate * 100;
      }
    }

    return {
      price,
      discountName,
      discountAmount,
      discountPercentage,
      discountedAmount: price - discountAmount,
    };
  };

  /**
   * 메뉴 총 금액 기준 할인 데이터 조회
   * @param {Menu} menu 메뉴 데이터
   * @param {number} qty 수량
   * @returns 할인 데이터
   */
  getTotalMenuAmount(menu, qty) {
    let discountName = '';
    let discountAmount = 0;

    // 토핑 추가 가격
    const toppingAddPrice = menu.toppingGroups
      .flatMap(toppingGroup => toppingGroup.toppings)
      .filter(topping => topping.isChecked === true)
      .map(topping => this.getToppingPrice(topping) * topping.qty)
      .reduce((a, b) => a + b, 0);

    // 세트메뉴 추가 가격
    const setMenuAddPrice = menu.setMenuGroups
      .flatMap(setMenuGroup => setMenuGroup.setMenus)
      .filter(setMenu => setMenu.isChecked === true)
      .map(setMenu => this.getSetMenuPrice(setMenu))
      .reduce((accumulator, addPrice) => accumulator + addPrice, 0);

    // 메뉴 단가
    const price = this.getMenuPrice(menu);

    // 메뉴 원금액
    const amount = (price + toppingAddPrice + setMenuAddPrice) * qty;

    const menuPriceInfo = this.getMenuPriceInfo(menu);
    discountName = menuPriceInfo.discountName;
    discountAmount = menuPriceInfo.discountAmount * qty;

    // 다른 할인이 없고 쿠폰이 적용된 경우 쿠폰 할인 데이터 계산
    if (discountAmount === 0 && menu.couponData) {
      const couponDiscountInfo = this.getCouponDiscount(amount, menu.couponData);
      discountName = couponDiscountInfo.discountName;
      discountAmount = couponDiscountInfo.discountAmount;
    }

    return {
      amount,
      discountName,
      discountAmount,
      discountedAmount: amount - discountAmount,
    };
  }

  /**
   * 메뉴 최종 금액 데이터 조회
   * @param {Menu} menu 메뉴 데이터
   * @param {number} qty 수량
   * @returns 메뉴 최종 금액 데이터
   */
  getMenuAmountInfo(menu, qty) {
    const amountInfo = {
      supplyAmount: 0,
      taxAmount: 0,
      totalAmount: 0,
    };

    if (!this.storeData || !menu) return amountInfo;

    // 할인 적용 후 금액
    const totalMenuAmountInfo = this.getTotalMenuAmount(menu, qty);

    // 세금 계산 변수
    const isTaxBeforeDiscount = this.storeData.isTaxBeforeDiscount;
    let taxType = this.storeData.tax1_type;
    let taxPercent = this.storeData.tax1_percent;
    let isTax = menu.isTax1;

    // 경감 세금인 경우 변수 값 변경
    if (this.isReduceTax && menu.isTax2 === '1') {
      taxType = this.storeData.tax2_type;
      taxPercent = this.storeData.tax2_percent;
      isTax = menu.isTax2;
    }

    // 공급가액 계산
    if (taxType === '1' && isTax === '1') {
      // 세금 별도
      if (isTaxBeforeDiscount) {
        // 할인 전 세금 계산
        amountInfo.supplyAmount = totalMenuAmountInfo.discountedAmount;
        amountInfo.taxAmount = Math.round(totalMenuAmountInfo.amount * (taxPercent / 100));
      } else {
        // 할인 후 세금 계산
        amountInfo.supplyAmount = totalMenuAmountInfo.discountedAmount;
        amountInfo.taxAmount = Math.round(amountInfo.supplyAmount * (taxPercent / 100));
      }
    } else if (taxType === '2' && isTax === '1') {
      // 세금 포함
      if (isTaxBeforeDiscount) {
        // 할인 전 세금 계산
        amountInfo.taxAmount =
          totalMenuAmountInfo.amount - Math.round(totalMenuAmountInfo.amount / (1 + taxPercent / 100));
        amountInfo.supplyAmount = totalMenuAmountInfo.discountedAmount - amountInfo.taxAmount;
      } else {
        // 할인 후 세금 계산
        amountInfo.supplyAmount = Math.round(totalMenuAmountInfo.discountedAmount / (1 + taxPercent / 100));
        amountInfo.taxAmount = totalMenuAmountInfo.discountedAmount - amountInfo.supplyAmount;
      }
    } else {
      // 면세
      amountInfo.supplyAmount = totalMenuAmountInfo.discountedAmount;
      amountInfo.taxAmount = 0;
    }

    amountInfo.totalAmount = amountInfo.supplyAmount + amountInfo.taxAmount;

    return amountInfo;
  }

  /**
   * 금액 데이터에 쿠폰 할인 적용
   * @param discountInfo 할인 데이터
   */
  getCouponDiscount(amount, couponData) {
    let discountName = '';
    let discountAmount = 0;

    // 다른 할인이 없는 경우에만 쿠폰할인 적용
    if (couponData) {
      // 할인 이름 세팅
      discountName = couponData.CouponName;
      if (couponData.DiscountType === '3') {
        // 금액 할인
        discountAmount = couponData.DiscountAmount;
      } else if (couponData.DiscountType === '4') {
        // 비율 할인
        discountAmount = Math.floor(amount * (couponData.DiscountAmount / 100));
      }
    }

    // 할인 금액이 더 큰 경우 할인 금액을 원금액과 동일하게 변경
    if (amount < discountAmount) {
      discountAmount = amount;
    }

    return {
      discountName,
      discountAmount,
    };
  }
}

export default MenuStore;
